import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, AlertCircle } from "lucide-react";
import type { Deck, CreateDeckData, UpdateDeckData } from "@/lib/api/deck-api";
import { parseDeckCode, formatDeckName } from "@/lib/utils/deck-library";
import type { Division } from "@/lib/types/deck-builder";
import type { UnitMap } from "@/lib/types/deck-builder";
import { tags } from "@/lib/types/tags";

interface DeckFormProps {
  deck?: Deck | null;
  deckId?: number;
  initialDeckCode?: string;
  divisions: Division[];
  unitMap: UnitMap;
  discordUsername: string;
  isVerified: boolean;
  onSubmit: (data: CreateDeckData | UpdateDeckData) => Promise<void>;
  onCancel?: () => void;
  showTitle?: boolean;
}

export function DeckForm({
  deck,
  deckId,
  initialDeckCode,
  divisions,
  unitMap,
  discordUsername,
  isVerified,
  onSubmit,
  onCancel,
  showTitle = true,
}: DeckFormProps) {
  const [deckCode, setDeckCode] = useState(deck?.deck_code || initialDeckCode || "");
  const [name, setName] = useState(deck?.name || "");
  const [isPublic, setIsPublic] = useState(deck?.is_public ?? true);
  const [divisionDescriptor, setDivisionDescriptor] = useState(deck?.division_descriptor || "");
  const [divisionName, setDivisionName] = useState("");
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Parse tags from deck prop (comma-separated string to array)
  const parseTagsFromDeck = (tagsString: string | null | undefined): string[] => {
    if (!tagsString || tagsString.trim() === '') return [];
    return tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
  };
  
  const [selectedTags, setSelectedTags] = useState<string[]>(() => 
    parseTagsFromDeck(deck?.tags)
  );
  
  // Update selected tags when deck changes
  useEffect(() => {
    if (deck?.tags !== undefined) {
      setSelectedTags(parseTagsFromDeck(deck.tags));
    }
  }, [deck?.tags]);

  // Update deck code when initialDeckCode changes (e.g., from URL parameter)
  useEffect(() => {
    if (initialDeckCode && !deck) {
      setDeckCode(initialDeckCode);
    }
  }, [initialDeckCode, deck]);

  // Parse deck code when it changes
  useEffect(() => {
    if (deckCode.trim() && divisions.length > 0 && Object.keys(unitMap).length > 0) {
      const timeoutId = setTimeout(async () => {
        setParsing(true);
        setError(null);
        try {
          const parsed = await parseDeckCode(deckCode, divisions, unitMap);
          if (parsed.isValid) {
            setDivisionDescriptor(parsed.divisionDescriptor);
            setDivisionName(parsed.divisionName);
            
            // Auto-format name for non-verified users
            if (!isVerified && !deck) {
              setName(formatDeckName(discordUsername, parsed.divisionName));
            }
          } else {
            setError(parsed.error || "Invalid deck code");
            setDivisionDescriptor("");
            setDivisionName("");
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : "Failed to parse deck code");
          setDivisionDescriptor("");
          setDivisionName("");
        } finally {
          setParsing(false);
        }
      }, 500); // Debounce parsing

      return () => clearTimeout(timeoutId);
    } else {
      setDivisionDescriptor("");
      setDivisionName("");
    }
  }, [deckCode, divisions, unitMap, isVerified, discordUsername, deck]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        // Enforce max 5 tags
        if (prev.length >= 5) {
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Format tags as comma-separated string
      const tagsString = selectedTags.length > 0 ? selectedTags.join(',') : undefined;

      if (deck || deckId) {
        // Update existing deck
        const updateData: UpdateDeckData = {
          deck_code: deckCode,
          division_descriptor: divisionDescriptor,
          is_public: isPublic,
          tags: tagsString,
        };
        
        if (isVerified) {
          updateData.name = name;
        }

        await onSubmit(updateData);
      } else {
        // Create new deck
        const createData: CreateDeckData = {
          deck_code: deckCode,
          division_descriptor: divisionDescriptor,
          is_public: isPublic,
          tags: tagsString,
        };

        if (isVerified) {
          createData.name = name;
        } else {
          createData.division_name = divisionName;
        }

        await onSubmit(createData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save deck");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = deckCode.trim().length > 0 && divisionDescriptor.length > 0 && (isVerified ? name.trim().length > 0 : true);
  const isUpdating = !!(deck || deckId);

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="pb-2">
          <h3 className="text-lg font-semibold">{deck ? "Edit Deck" : "Create New Deck"}</h3>
        </div>
      )}
      <div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deck-code">Deck Code</Label>
            <Textarea
              id="deck-code"
              value={deckCode}
              onChange={(e) => setDeckCode(e.target.value)}
              placeholder="Paste deck code here..."
              className="font-mono text-sm"
              rows={3}
              required
            />
            {parsing && (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Parsing deck code...
              </div>
            )}
            {divisionName && (
              <div className="text-sm text-muted-foreground">
                Division: {divisionName}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="deck-name">
              Deck Name
              {!isVerified && (
                <span className="text-xs text-muted-foreground ml-2">
                  (Auto-generated for non-verified users)
                </span>
              )}
            </Label>
            <Input
              id="deck-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isVerified ? "Enter deck name..." : "Auto-generated"}
              disabled={!isVerified}
              required={isVerified}
            />
          </div>

          <div className="space-y-2">
            <Label>Tags (Optional, max 5)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 p-3 border rounded-md max-h-48 overflow-y-auto">
              {tags.map((tag) => (
                <div key={tag} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tag-${tag}`}
                    checked={selectedTags.includes(tag)}
                    onCheckedChange={() => handleTagToggle(tag)}
                    disabled={!selectedTags.includes(tag) && selectedTags.length >= 5}
                  />
                  <Label
                    htmlFor={`tag-${tag}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {tag}
                  </Label>
                </div>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Selected: {selectedTags.length} / 5
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="is-public">Make deck public</Label>
            <Switch
              id="is-public"
              checked={isPublic}
              onCheckedChange={setIsPublic}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-4">
            <Button
              type="submit"
              disabled={!isFormValid || loading || parsing}
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {(deck || deckId) ? "Updating..." : "Creating..."}
                </>
              ) : (
                (deck || deckId) ? "Update Deck" : "Create Deck"
              )}
            </Button>
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

